import apiClient from './api';
import type {
  Zone,
  DeneignementZone,
  DeneignementZonesResponse,
  UpdateZoneStatutDTO,
  AssignEquipeDTO,
  Photo,
  MomentPhoto,
} from '@/types';

/**
 * Service API pour les zones de deneignement
 * SFD v2.1: Suivi terrain des zones comme sous-taches
 */
export const zoneService = {
  /**
   * Liste toutes les zones actives
   */
  async listZones(): Promise<Zone[]> {
    const response = await apiClient.get<Zone[]>('/zones');
    return response.data;
  },

  /**
   * Liste les zones Pony (1-4)
   */
  async listZonesPony(): Promise<Zone[]> {
    const response = await apiClient.get<Zone[]>('/zones/pony');
    return response.data;
  },

  /**
   * Liste les zones Tracteur (5-6)
   */
  async listZonesTracteur(): Promise<Zone[]> {
    const response = await apiClient.get<Zone[]>('/zones/tracteur');
    return response.data;
  },

  /**
   * Recupere le detail d'une zone
   */
  async getZone(id: number): Promise<Zone> {
    const response = await apiClient.get<Zone>(`/zones/${id}`);
    return response.data;
  },

  /**
   * Liste les zones d'une intervention avec leur statut
   */
  async listDeneignementZones(deneignementId: number): Promise<DeneignementZonesResponse> {
    const response = await apiClient.get<DeneignementZonesResponse>(
      `/deneignements/${deneignementId}/zones`
    );
    return response.data;
  },

  /**
   * Recupere le detail d'une zone d'intervention
   */
  async getDeneignementZone(deneignementId: number, zoneId: number): Promise<DeneignementZone> {
    const response = await apiClient.get<DeneignementZone>(
      `/deneignements/${deneignementId}/zones/${zoneId}`
    );
    return response.data;
  },

  /**
   * Met a jour le statut d'une zone
   */
  async updateStatut(
    deneignementId: number,
    zoneId: number,
    data: UpdateZoneStatutDTO
  ): Promise<{ zone: DeneignementZone; progression: number }> {
    const response = await apiClient.patch<{ zone: DeneignementZone; progression: number }>(
      `/deneignements/${deneignementId}/zones/${zoneId}/statut`,
      data
    );
    return response.data;
  },

  /**
   * Demarre les travaux sur une zone
   */
  async demarrerZone(
    deneignementId: number,
    zoneId: number
  ): Promise<{ zone: DeneignementZone; progression: number }> {
    const response = await apiClient.post<{ zone: DeneignementZone; progression: number }>(
      `/deneignements/${deneignementId}/zones/${zoneId}/demarrer`
    );
    return response.data;
  },

  /**
   * Termine les travaux sur une zone
   */
  async terminerZone(
    deneignementId: number,
    zoneId: number,
    commentaire?: string
  ): Promise<{ zone: DeneignementZone; progression: number; intervention_complete: boolean }> {
    const response = await apiClient.post<{
      zone: DeneignementZone;
      progression: number;
      intervention_complete: boolean;
    }>(
      `/deneignements/${deneignementId}/zones/${zoneId}/terminer`,
      commentaire ? { commentaire } : {}
    );
    return response.data;
  },

  /**
   * Affecte une equipe a une zone
   */
  async assignEquipe(
    deneignementId: number,
    zoneId: number,
    data: AssignEquipeDTO
  ): Promise<DeneignementZone> {
    const response = await apiClient.patch<DeneignementZone>(
      `/deneignements/${deneignementId}/zones/${zoneId}/equipe`,
      data
    );
    return response.data;
  },

  /**
   * Signale un incident sur un jalon en cours (RG-11, SFD jalonnement
   * Contact/Large) : le remet a "a faire" pour reprise par une autre equipe.
   */
  async signalerIncidentJalon(
    deneignementId: number,
    zoneId: number,
    data: { type: string; commentaire?: string }
  ): Promise<DeneignementZone> {
    const response = await apiClient.post<DeneignementZone>(
      `/deneignements/${deneignementId}/zones/${zoneId}/incident`,
      data
    );
    return response.data;
  },

  /**
   * Corrige l'horodatage (debut/fin) d'un jalon (RG-12, reserve a la
   * regulation - ROLE_GID_REGUL cote backend).
   */
  async corrigerHorodatageJalon(
    deneignementId: number,
    zoneId: number,
    data: { motif: string; heureDebut?: string; heureFin?: string }
  ): Promise<DeneignementZone> {
    const response = await apiClient.patch<DeneignementZone>(
      `/deneignements/${deneignementId}/zones/${zoneId}/correction`,
      data
    );
    return response.data;
  },

  /**
   * Upload une photo rattachee a un jalon (facultative, plusieurs par
   * jalon possibles - SFD jalonnement Contact/Large, Lot 4 - #121).
   */
  async uploadPhotoJalon(
    deneignementId: number,
    zoneId: number,
    moment: MomentPhoto,
    file: File
  ): Promise<Photo> {
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('moment', moment);

    const response = await apiClient.post<Photo>(
      `/deneignements/${deneignementId}/zones/${zoneId}/photos`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * Supprime une photo de jalon
   */
  async deletePhotoJalon(deneignementId: number, zoneId: number, photoId: number): Promise<void> {
    await apiClient.delete(`/deneignements/${deneignementId}/zones/${zoneId}/photos/${photoId}`);
  },
};

export default zoneService;
